// In-app QR scanner for the "Link to Outlook" step (state L). Reads QR 2 (the pairing
// token) shown in the Outlook pane. Uses jsQR — a pure-JS decoder that works on iOS
// Safari, where the native BarcodeDetector is unavailable. Manual short-code is the
// fallback (handled in app.ts). See design-handoff v1.10 §3.
import jsQR from "jsqr";

const DECODE_MAX = 720; // decode at ≤720px long edge — fast + plenty for a QR

export function qrSupported(): boolean {
  return true; // jsQR runs everywhere (incl. iOS)
}

// Resolves with the raw QR payload (a pairing URL/token). Caller extracts the token
// and calls redeemPairing({ token }).
export async function scanQR(video: HTMLVideoElement, signal: AbortSignal): Promise<string> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("no 2d context");

  return new Promise<string>((resolve, reject) => {
    const tick = () => {
      if (signal.aborted) return reject(new Error("aborted"));
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (video.readyState >= 2 && vw > 0) {
        const scale = Math.min(1, DECODE_MAX / Math.max(vw, vh));
        canvas.width = Math.round(vw * scale);
        canvas.height = Math.round(vh * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "attemptBoth" });
        if (code?.data) return resolve(code.data);
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

// Extract the pairing token from a scanned payload (a full URL or a bare token).
export function tokenFromPayload(payload: string): string {
  try {
    const u = new URL(payload);
    return u.searchParams.get("t") ?? u.pathname.split("/").pop() ?? payload;
  } catch {
    return payload.trim();
  }
}
